/**
 * @fileoverview Other resources as tiles: the organization's files for the
 * people teaching a program or a class (a safety plan, a floor plan, a
 * classroom layout).
 *
 * Small-to-medium tiles, two across on a phone and up to four on a wide
 * screen, so a handful reads at a glance and each is an obvious thing to tap.
 * A browser-renderable image shows itself; everything else shows what kind of
 * file it is. The whole tile opens the file, in a new tab, under its own name.
 *
 * The instructor's pages draw them read-only. The organization's pages pass
 * `onRename` and `onDelete`, which add two small controls to each tile.
 */

import { File, FileAudio, FileImage, FileText, Pencil, Presentation, Trash2 } from "lucide-react";
import { resourceKind, resourceMeta } from "@/lib/resources";

const KIND_ICONS = {
  pdf: FileText,
  document: FileText,
  slides: Presentation,
  audio: FileAudio,
  photo: FileImage,
  image: FileImage,
  file: File,
};

const controlCls =
  "flex h-7 w-7 items-center justify-center rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] disabled:opacity-50";

/**
 * @param {{
 *   resources: Array, onRename?: Function, onDelete?: Function, busyId?: string|null, testId?: string,
 * }} props
 */
export default function ResourceTiles({ resources, onRename, onDelete, busyId = null, testId = "resource-tiles" }) {
  if (!resources?.length) return null;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid={testId}>
      {resources.map((r) => {
        const kind = resourceKind(r.content_type);
        const KindIcon = KIND_ICONS[kind] || File;
        return (
          <div
            key={r.resource_id}
            className="relative overflow-hidden rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] transition-colors hover:border-[var(--studio-divider-strong)]"
          >
            <a
              href={r.url}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${r.title}`}
              data-testid={`resource-tile-${r.resource_id}`}
              className="block"
            >
              <div className="flex aspect-[16/10] items-center justify-center bg-[var(--studio-tint)]">
                {kind === "image" && r.url ? (
                  <img src={r.url} alt="" loading="lazy" className="h-full w-full object-cover" data-testid={`resource-thumb-${r.resource_id}`} />
                ) : (
                  <KindIcon className="h-9 w-9 text-[var(--text-tertiary)]" strokeWidth={1.5} aria-hidden="true" />
                )}
              </div>
              <div className="px-3 py-2.5">
                <div className="line-clamp-2 font-sans text-[14px] font-medium leading-snug text-[var(--text-primary)]">{r.title}</div>
                <div className="mt-0.5 font-sans text-[12px] text-[var(--text-tertiary)]">{resourceMeta(r)}</div>
              </div>
            </a>
            {(onRename || onDelete) && (
              <div className="absolute right-2 top-2 flex gap-1.5">
                {onRename && (
                  <button
                    type="button"
                    className={controlCls}
                    aria-label={`Rename ${r.title}`}
                    disabled={busyId === r.resource_id}
                    onClick={() => onRename(r)}
                    data-testid={`resource-rename-${r.resource_id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.7} />
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    className={`${controlCls} hover:text-[var(--state-danger)]`}
                    aria-label={`Delete ${r.title}`}
                    disabled={busyId === r.resource_id}
                    onClick={() => onDelete(r)}
                    data-testid={`resource-delete-${r.resource_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.7} />
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
